import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Atajos de teclado globales, al estilo de GitHub: `g` y luego una letra.
 *
 * **Por qué acordes y no una tecla suelta.** Una sola letra para navegar
 * significa que cualquier pulsación fuera de un campo te cambia de página, y en
 * una aplicación de dinero eso asusta: se pierde de vista lo que se estaba
 * mirando sin saber por qué. Con `g` de por medio hay una intención explícita, y
 * el acorde caduca solo al segundo y medio.
 *
 * **La guarda de «no mientras se escribe» es lo esencial.** Sin ella, teclear
 * «pagué la cena» en la descripción de un gasto te manda a Grupos por la `g`.
 * Es el fallo clásico de los atajos globales, y lo peor es que sólo lo sufre
 * quien escribe rápido.
 */

/**
 * El botón de «Nuevo gasto» que está **a la vista**, de los dos que hay.
 *
 * Hay dos en el árbol a la vez —el flotante de móvil y el de la barra lateral de
 * escritorio— porque las dos navegaciones conviven y se esconden con CSS, no con
 * JavaScript. Coger el primero con `querySelector` elegiría siempre el de la
 * lateral, que en móvil está en `display:none`: el clic funcionaría de rebote,
 * pero al abrir el selector de grupo lo montaría dentro de un contenedor oculto
 * y quedaría un diálogo invisible llevándose el foco.
 *
 * `offsetParent` es nulo exactamente cuando el elemento o alguno de sus padres
 * está en `display:none`, que es como se ocultan las dos navegaciones.
 *
 * Exportada para poder probarla: si esta regla viviera dentro del manejador de
 * teclas, el test tendría que copiarla y las dos copias divergirían.
 */
export function botonNuevoGastoVisible(): HTMLButtonElement | null {
  const candidatos = document.querySelectorAll<HTMLButtonElement>('[data-atajo="nuevo-gasto"]');
  return Array.from(candidatos).find((b) => b.offsetParent !== null) ?? null;
}

/**
 * Cómo se escribe el atajo de buscar en un sistema dado.
 *
 * `⌘` es la tecla Command y **sólo existe en los teclados de Apple**. La pista
 * estaba escrita a mano en tres sitios —la lateral, el pie de la paleta y esta
 * lista— y los tres decían `⌘K`, así que a quien entra desde Windows o Linux se
 * le anunciaba una tecla que no tiene. El atajo sí le funcionaba: el manejador
 * (`SearchPaletteProvider`) escucha `metaKey || ctrlKey` desde el principio. Lo
 * que estaba roto era el cartel, no la puerta.
 *
 * **Una pista y no las dos.** `⌘K / Ctrl K` obliga a todo el mundo a leer una
 * mitad que le sobra, y ocupa el doble de ancho en una lateral de 240px.
 *
 * Se pasa la plataforma en vez de leerla dentro para poder probar las dos ramas
 * sin tocar el `navigator` global — el mismo motivo por el que
 * `botonNuevoGastoVisible` está exportada.
 */
export function atajoDeBuscar(plataforma: string): string {
  // Los iPad e iPhone entran aquí por el teclado externo: llevan Command, no
  // Control. Safari de iPadOS ya dice «MacIntel», pero el de iPhone no.
  return /mac|iphone|ipad/i.test(plataforma) ? '⌘K' : 'Ctrl K';
}

/**
 * La pista de este navegador, calculada una vez.
 *
 * `userAgentData` es lo que no está deprecado, pero no lo tienen ni Safari ni
 * Firefox; `navigator.platform` sí lo tienen todos. Por eso van en ese orden y
 * no sólo el segundo.
 */
export const ATAJO_DE_BUSCAR = atajoDeBuscar(
  (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform
);

export interface Shortcut {
  /** Cómo se escribe, para la ayuda: `g h`, `n`, `?`. */
  keys: string;
  /** Clave i18n de la descripción. */
  labelKey: string;
  labelDefault: string;
}

export const SHORTCUTS: Shortcut[] = [
  { keys: 'g h', labelKey: 'shortcuts.go_home', labelDefault: 'Ir a inicio' },
  { keys: 'g g', labelKey: 'shortcuts.go_groups', labelDefault: 'Ir a grupos' },
  { keys: 'g s', labelKey: 'shortcuts.go_stats', labelDefault: 'Ir a mis números' },
  { keys: 'g n', labelKey: 'shortcuts.go_notifications', labelDefault: 'Ir a avisos' },
  { keys: 'n', labelKey: 'shortcuts.new_expense', labelDefault: 'Nuevo gasto' },
  { keys: ATAJO_DE_BUSCAR, labelKey: 'shortcuts.search', labelDefault: 'Buscar' },
  { keys: '?', labelKey: 'shortcuts.help', labelDefault: 'Ver esta ayuda' },
];

const DESTINOS: Record<string, string> = {
  h: '/',
  g: '/groups',
  s: '/stats',
  n: '/notifications',
};

/** ¿El foco está en algo donde el usuario está escribiendo? */
function escribiendo(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function useGlobalShortcuts(): { helpOpen: boolean; closeHelp: () => void } {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  // El acorde a medias vive en una ref y no en estado: cambiarlo no tiene que
  // repintar nada, y con estado cada pulsación de `g` redibujaría la aplicación
  // entera.
  const esperandoG = useRef(false);
  const temporizador = useRef<number | null>(null);
  // Espejo de `helpOpen` para el manejador, que se registra una sola vez.
  const ayudaAbierta = useRef(false);
  ayudaAbierta.current = helpOpen;

  useEffect(() => {
    const cancelar = () => {
      esperandoG.current = false;
      if (temporizador.current !== null) {
        window.clearTimeout(temporizador.current);
        temporizador.current = null;
      }
    };

    const handler = (e: KeyboardEvent) => {
      // Escape cierra la ayuda, y va **antes** que la guarda de escritura y que
      // la de modificadores. Una ventana de atajos que sólo se cierra con el
      // ratón es una contradicción, y era lo que había: sólo la cerraban la X y
      // el clic en el fondo. Se comprueba `helpOpen` a través de la ref porque
      // este `useEffect` sólo se vuelve a montar al cambiar `navigate`, así que
      // leer el estado directamente daría siempre el valor de la primera vuelta.
      if (e.key === 'Escape' && ayudaAbierta.current) {
        e.preventDefault();
        setHelpOpen(false);
        return;
      }

      if (escribiendo(e.target)) return;
      // Con modificador no es un atajo nuestro: `Cmd+K` lo lleva la paleta y
      // `Ctrl+R` es del navegador. Interceptarlos sería robarle teclas al
      // sistema.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (esperandoG.current) {
        const destino = DESTINOS[e.key.toLowerCase()];
        cancelar();
        if (destino) {
          e.preventDefault();
          navigate(destino);
        }
        return;
      }

      if (e.key === 'g') {
        esperandoG.current = true;
        // Segundo y medio: suficiente para el acorde, poco para que una `g`
        // suelta deje la aplicación esperando algo que ya nadie va a pulsar.
        temporizador.current = window.setTimeout(cancelar, 1500);
        return;
      }

      if (e.key === 'n') {
        // No hay ruta global de «nuevo gasto»: el destino depende de en qué
        // grupo estés, de cuántos tengas y de si hay que elegir. Eso vive en
        // `useNuevoGasto`, y aquí se pulsa el botón que lo usa en vez de
        // reimplementarlo.
        //
        // Hay **dos** botones en el DOM y sólo uno visible: el flotante de móvil
        // y el de la barra lateral de escritorio, que conviven porque las dos
        // navegaciones se esconden con CSS y no con JavaScript. Un
        // `querySelector` a secas cogería siempre el primero, que en escritorio
        // es el de la lateral y en móvil también —está antes en el árbol—, así
        // que en móvil se pulsaría un botón `display:none`. Funcionaría de
        // rebote, pero si abre el selector de grupo lo abriría **dentro** de la
        // lateral oculta: un diálogo invisible que se traga el foco.
        //
        // `offsetParent` es nulo justo cuando el elemento o alguno de sus padres
        // está en `display:none`, que es exactamente cómo se ocultan las dos.
        const visible = botonNuevoGastoVisible();
        if (visible) {
          e.preventDefault();
          visible.click();
        }
        return;
      }

      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(true);
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      cancelar();
    };
  }, [navigate]);

  return { helpOpen, closeHelp: () => setHelpOpen(false) };
}
